/**
 * Conversas no layout Simplificado (spec 018, US3 AS5 / US4): a lista do
 * inbox de sempre à esquerda; no centro, a conversa com a MESMA linha do tempo
 * e o MESMO compositor do atendimento. Conversa sem atendimento ganha
 * "Vincular a atendimento" (por BUSCA — não por número digitado) e "Criar
 * atendimento"; a vinculada abre o atendimento, onde a conversa já está.
 *
 * Nada de escrita nova: assumir, encerrar, transferir e vincular são os
 * endpoints do /chats (com as guardas de posse, pausa e visibilidade).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import {
  Ban,
  CheckCircle2,
  ChevronDown,
  Download,
  ExternalLink,
  FileText,
  Link2,
  LogOut,
  Pencil,
  Plus,
  ArrowRightLeft,
  Users,
} from 'lucide-react';
import { capitalize, type ChatDetail, type ChatMessage, type ChatTab, type TimelineEntry } from '@erp/shared';
import { Button } from '@/components/ui/button';
import { Input, Label, Select, Textarea } from '@/components/ui/input';
import { Dialog, EmptyState, Spinner } from '@/components/ui/misc';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuthUser } from '@/auth/use-auth';
import { usePermissions } from '@/auth/use-permissions';
import { useRealtimeConnected } from '@/lib/realtime';
import { useTerms } from '@/shared/tenant-profile';
import { ChatListPane } from '@/features/chats/chat-list-pane';
import { chatDetailOptions, chatRelatedOptions, useChatMessages } from '@/features/chats/chats-queries';
import {
  downloadChatTranscript,
  useLeaveChat,
  useMarkChatRead,
  useResendChatMessage,
  useUpdateChat,
} from '@/features/chats/chats-mutations';
import { ArchiveChatDialog, TransferChatDialog } from '@/features/chats/chat-dialogs';
import { ContactSheet } from '@/features/chats/contact-sheet';
import {
  ChannelChip,
  ChatAvatar,
  ExternalBotBadge,
  OriginBadge,
  StatusBadge,
  WaitingAttendantBadge,
  chatDisplayName,
  errorMessage,
  formatRelativeTime,
  formatWaPhone,
} from '@/features/chats/chats-format';
import { ClientPicker, type ClientRef } from '@/features/tickets/ClientPicker';
import {
  creatableDesks,
  desksListQuery,
  ticketsListQuery,
  useOperatedClientIds,
} from '@/features/tickets/tickets-queries';
import { useCreateTicket } from '@/features/tickets/tickets-mutations';
import { useDebouncedValue } from '@/features/tickets/use-debounced-value';
import { useAssumeWork } from '@/features/work/work-actions';
import { WORK_KEY } from '@/features/work/work-queries';
import { Timeline } from '@/features/atendimentos/timeline';
import { Composer } from '@/features/atendimentos/composer';
import { useDrafts } from '@/features/atendimentos/drafts';
import { SituationChip } from '@/features/atendimentos/situation-chip';

export const conversasSearchSchema = z.object({
  chat: z.coerce.number().int().positive().optional().catch(undefined),
});
export type ConversasSearch = z.infer<typeof conversasSearchSchema>;

export function ConversasPage() {
  const search = conversasSearchSchema.parse(useSearch({ strict: false }));
  const navigate = useNavigate();
  const [tab, setTab] = useState<ChatTab>('inbox');
  const select = (chatId: number | null) =>
    void navigate({ to: '/s/conversas', search: chatId ? { chat: chatId } : {}, replace: true });

  // altura útil: 100dvh − cabeçalho (3rem) − padding do main (2rem)
  return (
    <div className="flex h-[calc(100dvh-5rem)] min-h-[420px] gap-4">
      <ChatListPane tab={tab} onTabChange={setTab} selectedId={search.chat ?? null} onSelect={select} />
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden rounded-lg border bg-card shadow-xs">
        {search.chat ? (
          <ConversationView
            key={search.chat}
            chatId={search.chat}
            onGone={() => select(null)}
            onSelectChat={(id) => select(id)}
          />
        ) : (
          <EmptyState message="Selecione uma conversa" className="h-full" />
        )}
      </div>
    </div>
  );
}

/** a mensagem do /chats no formato da linha do tempo — o mesmo desenho do atendimento */
function toEntry(m: ChatMessage, chat: ChatDetail): TimelineEntry {
  return {
    kind: 'chat_message',
    id: m.id,
    at: m.createdAt,
    chatId: m.chatId,
    chatOrigin: chat.origin,
    direction: m.direction,
    authorType: m.authorType,
    authorName: m.authorName,
    internal: m.internal,
    text: m.text,
    contentType: m.contentType,
    mediaMime: m.mediaMime,
    mediaFilename: m.mediaFilename,
    mediaSize: m.mediaSize,
    caption: m.caption,
    mediaUrl: m.mediaUrl,
    deliveryStatus: m.deliveryStatus,
    // spec 028: o motivo da falha e o "tentar de novo" também no layout simplificado
    deliveryError: m.deliveryError,
    resentAt: m.resentAt,
  };
}

function ConversationView({
  chatId,
  onGone,
  onSelectChat,
}: {
  chatId: number;
  onGone: () => void;
  /** abre outra conversa (spec 028: as da mesma pessoa) */
  onSelectChat: (chatId: number) => void;
}) {
  const me = useAuthUser();
  const terms = useTerms();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { can, ready } = usePermissions();
  const realtimeUp = useRealtimeConnected();
  const detailQ = useQuery({
    ...chatDetailOptions(chatId),
    refetchInterval: realtimeUp ? 60_000 : 8_000,
    retry: (n, e) => !(e && typeof e === 'object' && 'status' in e && e.status === 404) && n < 3,
  });
  const chat = detailQ.data;
  const messagesQ = useChatMessages(chatId, chat?.origin);
  const drafts = useDrafts(`erp.draft.chat.${chatId}`);
  const markRead = useMarkChatRead();
  const leave = useLeaveChat();
  const resend = useResendChatMessage(chatId);
  const { assume, pending: assuming, error: assumeError, dialog } = useAssumeWork();
  const [dialogOpen, setDialogOpen] = useState<'link' | 'create' | 'transfer' | 'finish' | 'cancel' | null>(null);
  // spec 056: o painel "Contato", aberto pelo nome (o mesmo do chat clássico)
  const [contactOpen, setContactOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const entries = useMemo(
    () => (chat ? (messagesQ.data ?? []).map((m) => toEntry(m, chat)) : []),
    [messagesQ.data, chat]
  );

  // ler a conversa marca como lida — ao abrir e a cada mensagem nova do cliente
  const lastInbound = useMemo(
    () => [...(messagesQ.data ?? [])].reverse().find((m) => m.direction === 'inbound')?.id ?? null,
    [messagesQ.data]
  );
  const markReadRef = useRef(markRead.mutate);
  markReadRef.current = markRead.mutate;
  useEffect(() => {
    markReadRef.current(chatId);
  }, [chatId, lastInbound]);

  // fim da conversa sempre à vista
  const scrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [entries.length]);

  const gone = detailQ.isError && (detailQ.error as { status?: number } | null)?.status === 404;
  useEffect(() => {
    if (gone) onGone();
  }, [gone, onGone]);

  if (detailQ.isPending) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner className="size-6" />
      </div>
    );
  }
  if (!chat) return <EmptyState message={`Erro ao carregar a conversa: ${errorMessage(detailQ.error)}`} className="h-full" />;

  const inbox = chat.status === 'inbox';
  const archived = chat.status === 'archived';
  const mine = chat.status === 'in_attendance' && chat.responsible?.id === me?.id;
  const othersAttending = chat.status === 'in_attendance' && !mine;
  const manageOthers = ready && can('chat.manage_others');
  const canClose = inbox || mine || (othersAttending && manageOthers);
  const onSent = () => {
    void qc.invalidateQueries({ queryKey: WORK_KEY });
  };

  return (
    <>
      <div className="flex shrink-0 flex-wrap items-center gap-2.5 border-b px-4 py-2.5">
        <ChatAvatar name={chatDisplayName(chat)} />
        <div className="min-w-0 flex-1">
          {/* flex-wrap: com os selos a linha quebra em vez de o nome sumir */}
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <button
              type="button"
              onClick={() => setContactOpen(true)}
              title="Editar contato"
              aria-haspopup="dialog"
              className="group inline-flex min-w-0 items-center gap-1 rounded-sm text-left text-sm font-semibold hover:text-primary focus-visible:ring-2 focus-visible:ring-ring/50 focus-visible:outline-none"
            >
              <span className="truncate">{chatDisplayName(chat)}</span>
              <Pencil
                aria-hidden
                className="size-3 shrink-0 text-muted-foreground opacity-60 group-hover:opacity-100"
              />
            </button>
            <OriginBadge origin={chat.origin} className="px-1.5 py-0 text-[10px]" />
            <StatusBadge status={chat.status} canceled={chat.canceled} className="px-1.5 py-0 text-[10px]" />
            {/* spec 021: o bot externo ainda conduz — ou acabou de transferir */}
            {chat.externalBotActive ? <ExternalBotBadge /> : null}
            {inbox && chat.waitingAttendantSince ? <WaitingAttendantBadge /> : null}
          </div>
          {/* spec 028 (FR-003): por qual número da empresa a pessoa fala */}
          {chat.channel ? <ChannelChip channel={chat.channel} className="max-w-full text-xs" /> : null}
          <div className="truncate text-xs text-muted-foreground">
            {[
              chat.client?.name,
              formatWaPhone(chat.waContact ?? chat.requestorPhone),
              chat.desk?.name,
              chat.responsible ? `com ${chat.responsible.name}` : null,
            ]
              .filter(Boolean)
              .join(' · ')}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          {chat.origin === 'whatsapp' ? <RelatedChatsMenu chat={chat} onSelectChat={onSelectChat} /> : null}
          {inbox ? (
            <Button size="sm" disabled={assuming} onClick={() => assume({ kind: 'chat', chatId: chat.id })}>
              {assuming ? <Spinner className="size-4 border-primary-foreground" /> : null}
              Assumir
            </Button>
          ) : null}
          {chat.ticketNumber !== null ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                void navigate({
                  to: '/s/atendimentos/$ticketNumber',
                  params: { ticketNumber: String(chat.ticketNumber) },
                })
              }
            >
              <ExternalLink />
              {capitalize(terms.ticket)} #{chat.ticketNumber}
            </Button>
          ) : (
            <>
              <Button variant="outline" size="sm" onClick={() => setDialogOpen('link')}>
                <Link2 />
                Vincular a {terms.ticket}
              </Button>
              <Button variant="outline" size="sm" onClick={() => setDialogOpen('create')}>
                <Plus />
                Criar {terms.ticket}
              </Button>
            </>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                Ações
                <ChevronDown />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              {mine ? (
                <>
                  <DropdownMenuItem onSelect={() => setDialogOpen('transfer')}>
                    <ArrowRightLeft />
                    Transferir
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => leave.mutate(chat.id, { onError: (e) => setError(errorMessage(e)) })}>
                    <LogOut />
                    Sair da conversa
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                </>
              ) : null}
              {canClose && !archived ? (
                <DropdownMenuItem onSelect={() => setDialogOpen('finish')}>
                  <CheckCircle2 />
                  Finalizar
                </DropdownMenuItem>
              ) : null}
              {canClose && !archived && ready && can('chat.cancel') ? (
                <DropdownMenuItem variant="destructive" onSelect={() => setDialogOpen('cancel')}>
                  <Ban />
                  Cancelar
                </DropdownMenuItem>
              ) : null}
              <DropdownMenuItem onSelect={() => void downloadChatTranscript(chat.id, 'pdf').catch(() => setError('Não foi possível baixar.'))}>
                <Download />
                Baixar conversa
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => void downloadChatTranscript(chat.id, 'txt').catch(() => setError('Não foi possível baixar.'))}>
                <FileText />
                Baixar como texto (.txt)
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        {assumeError || error ? <p className="w-full text-xs text-destructive">{assumeError ?? error}</p> : null}
      </div>

      <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto bg-background px-4 py-3">
        <Timeline
          ticketNumber={0}
          entries={entries}
          isPending={messagesQ.isPending}
          isError={messagesQ.isError}
          hasOlder={false}
          loadingOlder={false}
          onLoadOlder={() => undefined}
          // spec 060: a conversa abre rolada para o fim — o switch fica preso no topo
          stickySwitch
          // spec 028: quem segura a conversa reenvia a resposta que falhou
          onResendChatMessage={
            mine && chat.origin === 'whatsapp'
              ? (_chatId, messageId) => resend.mutate(messageId, { onError: (e) => setError(errorMessage(e)) })
              : null
          }
        />
      </div>

      <div className="shrink-0 border-t p-2">
        <Composer
          key={`chat-${chat.id}`}
          channels={[
            {
              channel: { kind: 'chat', chatId: chat.id },
              label: `${chat.origin === 'whatsapp' ? 'WhatsApp' : 'Conversa'} · ${chat.requestorName}`,
            },
          ]}
          noteTarget={archived ? { kind: 'none', reason: 'Conversa encerrada.' } : { kind: 'chat', chatId: chat.id }}
          ticketNumber={null}
          requestorEmail={null}
          commentBlockedReason={null}
          defaultChannel={{ kind: 'chat', chatId: chat.id }}
          drafts={drafts}
          autoFocus={false}
          onSent={onSent}
        />
      </div>

      {dialogOpen === 'link' ? <LinkTicketSearchDialog chat={chat} onClose={() => setDialogOpen(null)} /> : null}
      <ContactSheet chatId={chat.id} open={contactOpen} onClose={() => setContactOpen(false)} />
      {dialogOpen === 'create' ? <CreateTicketFromChatDialog chat={chat} onClose={() => setDialogOpen(null)} /> : null}
      {dialogOpen === 'transfer' ? <TransferChatDialog chat={chat} onClose={() => setDialogOpen(null)} /> : null}
      {dialogOpen === 'finish' || dialogOpen === 'cancel' ? (
        <ArchiveChatDialog chat={chat} canceled={dialogOpen === 'cancel'} onClose={() => setDialogOpen(null)} />
      ) : null}
      {dialog}
    </>
  );
}

/**
 * As outras conversas da MESMA pessoa (spec 028, FR-002) no layout
 * simplificado, que não tem coluna lateral: um menu no cabeçalho — o número da
 * empresa, a data e a situação de cada uma; um clique abre.
 */
function RelatedChatsMenu({ chat, onSelectChat }: { chat: ChatDetail; onSelectChat: (chatId: number) => void }) {
  const relatedQ = useQuery(chatRelatedOptions(chat.id));
  const items = relatedQ.data ?? [];
  if (items.length === 0) return null;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" title="Outras conversas desta pessoa">
          <Users />
          Outras conversas ({items.length})
          <ChevronDown />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72">
        {items.map((r) => (
          <DropdownMenuItem key={r.id} onSelect={() => onSelectChat(r.id)} className="flex-col items-stretch gap-0.5">
            <span className="flex items-center justify-between gap-2">
              {r.channel ? <ChannelChip channel={r.channel} short className="text-xs text-foreground" /> : 'WhatsApp'}
              <span className="text-[11px] text-muted-foreground tabular-nums">
                {formatRelativeTime(r.lastMessageAt ?? r.createdAt)}
              </span>
            </span>
            <span className="flex items-center gap-1.5">
              <StatusBadge status={r.status} canceled={r.canceled} className="px-1.5 py-0 text-[10px]" />
              {r.waContact !== chat.waContact && r.waContact ? (
                <span className="text-[11px] text-muted-foreground">{formatWaPhone(r.waContact)}</span>
              ) : null}
            </span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * "Vincular a atendimento" por BUSCA (US3 AS5): abre com os atendimentos
 * abertos do cliente da conversa; a busca é a mesma da lista (número, assunto,
 * cliente, contato, identificador), em todas as mesas.
 */
function LinkTicketSearchDialog({ chat, onClose }: { chat: ChatDetail; onClose: () => void }) {
  const terms = useTerms();
  const update = useUpdateChat(chat.id);
  const [q, setQ] = useState('');
  const debounced = useDebouncedValue(q, 300);
  const listQ = useQuery(
    ticketsListQuery({
      ...(debounced.trim() ? { search: debounced.trim() } : chat.client ? { client_ids: [chat.client.id] } : {}),
      status: debounced.trim() ? 'all' : 'open',
      sort: 'activity',
      order: 'desc',
      limit: 10,
      offset: 0,
    })
  );
  const [error, setError] = useState<string | null>(null);
  const items = listQ.data?.items ?? [];

  return (
    <Dialog open onClose={onClose} title={`Vincular a um ${terms.ticket}`} className="sm:max-w-xl">
      <div className="flex flex-col gap-3">
        <Input
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={`Buscar por nº, assunto, ${terms.client}, ${terms.contact} ou ${terms.resourceId}`}
          aria-label={`Buscar ${terms.ticket}`}
        />
        <p className="text-xs text-muted-foreground">
          {debounced.trim()
            ? `Resultados da busca em todas as ${terms.desks}.`
            : chat.client
              ? `${capitalize(terms.tickets)} abertos de ${chat.client.name}.`
              : `Busque o ${terms.ticket} que esta conversa trata.`}
        </p>
        <ul className="max-h-80 divide-y overflow-y-auto rounded-md border">
          {listQ.isPending ? (
            <li className="flex justify-center p-4">
              <Spinner className="size-5" />
            </li>
          ) : items.length === 0 ? (
            <li className="p-4 text-center text-sm text-muted-foreground">Nenhum {terms.ticket} encontrado.</li>
          ) : (
            items.map((t) => (
              <li key={t.ticketNumber}>
                <button
                  type="button"
                  disabled={update.isPending}
                  onClick={() =>
                    update.mutate(
                      { ticketNumber: t.ticketNumber },
                      { onSuccess: onClose, onError: (e) => setError(errorMessage(e)) }
                    )
                  }
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-muted/50"
                >
                  <span className="shrink-0 font-semibold text-primary tabular-nums">#{t.ticketNumber}</span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate">{t.title}</span>
                    <span className="block truncate text-xs text-muted-foreground">
                      {[t.client.name, t.resourceLabel, t.desk.name].filter(Boolean).join(' · ')}
                    </span>
                  </span>
                  <SituationChip situation={t.situation} />
                </button>
              </li>
            ))
          )}
        </ul>
        {error ? <p className="text-sm text-destructive">{error}</p> : null}
        <div className="flex justify-end">
          <Button variant="outline" size="sm" onClick={onClose}>
            Fechar
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

/**
 * "Criar atendimento" a partir da conversa: o ticket nasce com o cliente, o
 * contato e o telefone dela, e a conversa é vinculada a ele — a mesma criação
 * (POST /tickets) e o mesmo vínculo (PUT /chats/:id) de sempre.
 */
function CreateTicketFromChatDialog({ chat, onClose }: { chat: ChatDetail; onClose: () => void }) {
  const terms = useTerms();
  const navigate = useNavigate();
  const create = useCreateTicket();
  const link = useUpdateChat(chat.id);
  const desksQ = useQuery(desksListQuery());
  const [client, setClient] = useState<ClientRef | null>(chat.client ? { id: chat.client.id, name: chat.client.name } : null);
  // mesa espelhada só recebe ticket de cliente operado no ERP (spec 024) — a
  // lista acompanha o cliente escolhido; de outro cliente, nem entra
  const operated = useOperatedClientIds();
  const desks = creatableDesks(desksQ.data ?? [], operated, client?.id ?? null);
  const [deskId, setDeskId] = useState<number | ''>(chat.desk && desks.some((d) => d.id === chat.desk!.id) ? chat.desk.id : '');
  const [title, setTitle] = useState(`Conversa com ${chat.requestorName}`);
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);
  const busy = create.isPending || link.isPending;
  // a mesa escolhida pode ter saído da lista ao trocar o cliente
  const effectiveDesk = deskId !== '' && desks.some((d) => d.id === deskId) ? deskId : desks[0]?.id;

  const submit = () => {
    setError(null);
    if (!client || !effectiveDesk || !title.trim()) {
      setError(`Escolha a ${terms.desk}, o ${terms.client} e o assunto.`);
      return;
    }
    create.mutate(
      {
        title: title.trim(),
        description: description.trim() || null,
        clientId: client.id,
        deskId: effectiveDesk,
        requestorName: chat.requestorName,
        requestorPhone: chat.waContact ?? chat.requestorPhone,
      },
      {
        onSuccess: (ticket) =>
          link.mutate(
            { ticketNumber: ticket.ticketNumber, ...(chat.client ? {} : { clientId: client.id }) },
            {
              onSuccess: () =>
                void navigate({
                  to: '/s/atendimentos/$ticketNumber',
                  params: { ticketNumber: String(ticket.ticketNumber) },
                }),
              onError: (e) => setError(`${capitalize(terms.ticket)} #${ticket.ticketNumber} criado, mas o vínculo falhou: ${errorMessage(e)}`),
            }
          ),
        onError: (e) => setError(errorMessage(e)),
      }
    );
  };

  return (
    <Dialog open onClose={onClose} title={`Criar ${terms.ticket} desta conversa`} className="sm:max-w-xl">
      <div className="flex flex-col gap-3">
        <div className="space-y-1.5">
          <Label htmlFor="ctc-desk">{capitalize(terms.desk)}</Label>
          <Select id="ctc-desk" value={effectiveDesk ?? ''} onChange={(e) => setDeskId(e.target.value ? Number(e.target.value) : '')}>
            {desks.map((d) => (
              <option key={d.id} value={d.id}>
                {d.displayName}
              </option>
            ))}
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label>{capitalize(terms.client)}</Label>
          <ClientPicker selected={client} onSelect={setClient} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="ctc-title">Assunto</Label>
          <Input id="ctc-title" value={title} onChange={(e) => setTitle(e.target.value)} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="ctc-desc">Descrição (opcional)</Label>
          <Textarea id="ctc-desc" value={description} onChange={(e) => setDescription(e.target.value)} className="min-h-20" />
        </div>
        {error ? <p className="text-sm text-destructive">{error}</p> : null}
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose}>
            Cancelar
          </Button>
          <Button size="sm" disabled={busy || desks.length === 0} onClick={submit}>
            {busy ? <Spinner className="size-4 border-primary-foreground" /> : <Plus />}
            Criar e vincular
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
